import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, CircleIcon, FileTextIcon, MessageSquareIcon, TargetIcon, ChevronRightIcon, BotIcon, ShieldIcon, CloudIcon, DatabaseIcon, LineChartIcon, AwardIcon } from 'lucide-react';
const careerPaths = [{
  id: 'cloud',
  title: 'Cloud Solutions Architect',
  endSalary: '$155K/yr',
  icon: CloudIcon,
  roadmap: [{
    phase: 'Year 0-1: Foundation',
    role: 'IT Support / Help Desk',
    salary: '$35K - $45K',
    desc: 'Build your technical foundation with hands-on support experience.',
    skills: ['CompTIA A+', 'Basic Networking', 'Linux Fundamentals'],
    certs: ['CompTIA A+']
  }, {
    phase: 'Year 1-3: Cloud Practitioner',
    role: 'Junior Cloud Engineer',
    salary: '$55K - $75K',
    desc: 'Deploy and manage cloud infrastructure under supervision.',
    skills: ['AWS/Azure Basics', 'IaC', 'Scripting'],
    certs: ['AWS Cloud Practitioner', 'AWS Solutions Architect Assoc']
  }, {
    phase: 'Year 3-5: Cloud Engineer',
    role: 'Cloud Engineer',
    salary: '$80K - $110K',
    desc: 'Design and implement complex cloud solutions independently.',
    skills: ['Advanced Networking', 'Security', 'CI/CD', 'Kubernetes'],
    certs: ['AWS DevOps Professional']
  }, {
    phase: 'Year 5-7: Senior Engineer',
    role: 'Senior Cloud Engineer',
    salary: '$110K - $135K',
    desc: 'Lead cloud initiatives and mentor junior engineers.',
    skills: ['Architecture Patterns', 'Cost Optimization', 'Team Leadership'],
    certs: ['AWS Solutions Architect Professional']
  }, {
    phase: 'Year 7-10: Architect',
    role: 'Cloud Solutions Architect',
    salary: '$135K - $155K+',
    desc: 'Define cloud strategy for entire organizations.',
    skills: ['Enterprise Architecture', 'Stakeholder Management', 'Strategy'],
    certs: ['TOGAF', 'AWS Specialty Certs']
  }]
}, {
  id: 'ai',
  title: 'AI & Automation Specialist',
  endSalary: '$145K/yr',
  icon: BotIcon,
  roadmap: [{
    phase: 'Year 0-1: Foundation',
    role: 'Data Annotator / QA',
    salary: '$40K - $50K',
    desc: 'Understand data structures and AI model training basics.',
    skills: ['Data Quality', 'Basic Python', 'QA Processes'],
    certs: ['Google Data Analytics']
  }, {
    phase: 'Year 1-3: Prompt Engineer',
    role: 'AI Operations Associate',
    salary: '$65K - $85K',
    desc: 'Optimize LLM outputs and build basic automations.',
    skills: ['Prompt Engineering', 'API Integration', 'Zapier/Make'],
    certs: ['OpenAI API Fundamentals']
  }, {
    phase: 'Year 3-5: Automation Engineer',
    role: 'AI Automation Engineer',
    salary: '$90K - $115K',
    desc: 'Build complex agentic workflows and integrate AI into products.',
    skills: ['LangChain', 'Vector Databases', 'Python Automation'],
    certs: ['AWS Machine Learning Specialty']
  }, {
    phase: 'Year 5-7: Senior AI Engineer',
    role: 'Senior AI Solutions Engineer',
    salary: '$115K - $135K',
    desc: 'Design enterprise-scale AI systems and fine-tune models.',
    skills: ['Model Fine-tuning', 'MLOps', 'System Architecture'],
    certs: ['Google Professional ML Engineer']
  }, {
    phase: 'Year 7-10: AI Architect',
    role: 'Principal AI Architect',
    salary: '$135K - $145K+',
    desc: 'Lead AI strategy and autonomous agent development.',
    skills: ['Agentic AI Design', 'AI Ethics', 'Strategic Leadership'],
    certs: ['Stanford AI Professional Program']
  }]
}, {
  id: 'cyber',
  title: 'Cybersecurity Analyst',
  endSalary: '$125K/yr',
  icon: ShieldIcon,
  roadmap: [{
    phase: 'Year 0-1: Foundation',
    role: 'IT Help Desk',
    salary: '$35K - $45K',
    desc: 'Learn network fundamentals and basic troubleshooting.',
    skills: ['Networking', 'OS Administration', 'Troubleshooting'],
    certs: ['CompTIA Network+']
  }, {
    phase: 'Year 1-3: SOC Analyst I',
    role: 'Junior Security Analyst',
    salary: '$60K - $75K',
    desc: 'Monitor security alerts and perform initial triage.',
    skills: ['SIEM (Splunk)', 'Log Analysis', 'Incident Response Basics'],
    certs: ['CompTIA Security+', 'CySA+']
  }, {
    phase: 'Year 3-5: SOC Analyst II',
    role: 'Cybersecurity Analyst',
    salary: '$80K - $100K',
    desc: 'Investigate complex threats and manage security tools.',
    skills: ['Threat Hunting', 'Vulnerability Management', 'Python'],
    certs: ['CEH', 'Splunk Core Certified Power User']
  }, {
    phase: 'Year 5-7: Senior Analyst',
    role: 'Senior Security Analyst',
    salary: '$100K - $115K',
    desc: 'Lead incident response and mentor junior analysts.',
    skills: ['Advanced Forensics', 'Malware Analysis', 'Leadership'],
    certs: ['CISSP (Associate)']
  }, {
    phase: 'Year 7-10: Security Manager',
    role: 'Information Security Manager',
    salary: '$115K - $125K+',
    desc: 'Manage security operations and define security policies.',
    skills: ['Risk Management', 'Compliance', 'Strategic Planning'],
    certs: ['CISM', 'CISSP']
  }]
}, {
  id: 'data',
  title: 'Data Engineer',
  endSalary: '$135K/yr',
  icon: DatabaseIcon,
  roadmap: [{
    phase: 'Year 0-1: Foundation',
    role: 'Data Entry / Junior Analyst',
    salary: '$40K - $55K',
    desc: 'Work with spreadsheets, basic SQL, and data cleaning.',
    skills: ['Excel', 'Basic SQL', 'Data Cleaning'],
    certs: ['Google Data Analytics']
  }, {
    phase: 'Year 1-3: Data Analyst',
    role: 'Data Analyst',
    salary: '$65K - $85K',
    desc: 'Create dashboards and write complex queries.',
    skills: ['Advanced SQL', 'Tableau/PowerBI', 'Python (Pandas)'],
    certs: ['Tableau Desktop Specialist']
  }, {
    phase: 'Year 3-5: Junior Data Engineer',
    role: 'Data Engineer',
    salary: '$90K - $110K',
    desc: 'Build ETL pipelines and manage data warehouses.',
    skills: ['ETL/ELT', 'Snowflake/BigQuery', 'Airflow'],
    certs: ['AWS Data Engineer Associate']
  }, {
    phase: 'Year 5-7: Senior Data Engineer',
    role: 'Senior Data Engineer',
    salary: '$110K - $125K',
    desc: 'Design scalable data architectures and streaming pipelines.',
    skills: ['Spark', 'Kafka', 'Data Modeling'],
    certs: ['Databricks Certified Data Engineer Professional']
  }, {
    phase: 'Year 7-10: Data Architect',
    role: 'Principal Data Architect',
    salary: '$125K - $135K+',
    desc: 'Define enterprise data strategy and governance.',
    skills: ['Data Governance', 'System Architecture', 'Leadership'],
    certs: ['Google Professional Data Engineer']
  }]
}, {
  id: 'revops',
  title: 'Sales & RevOps Manager',
  endSalary: '$120K/yr',
  icon: LineChartIcon,
  roadmap: [{
    phase: 'Year 0-1: Foundation',
    role: 'Sales Development Rep (SDR)',
    salary: '$45K - $60K',
    desc: 'Learn the sales process and CRM basics from the ground up.',
    skills: ['Cold Outreach', 'CRM Basics', 'Communication'],
    certs: ['HubSpot Sales Software']
  }, {
    phase: 'Year 1-3: CRM Admin',
    role: 'Salesforce Administrator',
    salary: '$65K - $80K',
    desc: 'Manage CRM systems and support sales teams with data.',
    skills: ['Salesforce Admin', 'Data Hygiene', 'Basic Reporting'],
    certs: ['Salesforce Certified Administrator']
  }, {
    phase: 'Year 3-5: RevOps Analyst',
    role: 'Revenue Operations Analyst',
    salary: '$85K - $100K',
    desc: 'Analyze funnel metrics and optimize sales/marketing processes.',
    skills: ['Process Optimization', 'Advanced Analytics', 'Tool Integration'],
    certs: ['Revenue Operations Certification (HubSpot)']
  }, {
    phase: 'Year 5-7: RevOps Manager',
    role: 'RevOps Manager',
    salary: '$100K - $115K',
    desc: 'Lead operations strategy across sales, marketing, and success.',
    skills: ['Cross-functional Leadership', 'Forecasting', 'Strategy'],
    certs: ['Salesforce Advanced Administrator']
  }, {
    phase: 'Year 7-10: Director of RevOps',
    role: 'Director of Revenue Operations',
    salary: '$115K - $120K+',
    desc: 'Drive GTM strategy and align all revenue-generating teams.',
    skills: ['GTM Strategy', 'Executive Reporting', 'Team Building'],
    certs: ['Executive Leadership Programs']
  }]
}];
export function CareerPathways() {
  const [selectedPathId, setSelectedPathId] = useState('cloud');
  const selectedPath = careerPaths.find(p => p.id === selectedPathId) || careerPaths[0];
  return <div className="space-y-8 pb-8" data-id="element-55">
      {/* Header */}
      <div data-id="element-56">
        <h1 className="text-2xl font-bold text-surface-900 tracking-tight" data-id="element-57">
          Career Pathways
        </h1>
        <p className="text-surface-500 text-sm mt-1" data-id="element-58">
          Visualize your future and the steps to get there.
        </p>
      </div>

      {/* Where You Are Today */}
      <div className="bg-primary-900 text-white p-6 rounded-2xl shadow-lg flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden" data-id="element-59">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-primary-600 rounded-full blur-3xl opacity-30" data-id="element-60" />

        <div className="relative z-10 flex-1" data-id="element-61">
          <h2 className="text-sm font-semibold text-primary-200 uppercase tracking-wider mb-1" data-id="element-62">
            Where You Are Today
          </h2>
          <h3 className="text-2xl font-bold mb-4" data-id="element-63">Career Starter</h3>
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-64">
            <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-medium" data-id="element-65">
              Basic IT Knowledge
            </span>
            <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-medium" data-id="element-66">
              Customer Service
            </span>
            <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-medium" data-id="element-67">
              Eager to Learn
            </span>
          </div>
          <p className="text-primary-100 text-sm" data-id="element-68">
            You are currently in Year 0. Select a path below to see your 10-year
            roadmap.
          </p>
        </div>

        <div className="relative z-10 bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/20 flex flex-col items-center min-w-[150px]" data-id="element-69">
          <span className="text-xs font-medium text-primary-200 mb-2" data-id="element-70">
            Readiness Score
          </span>
          <div className="text-3xl font-bold text-white" data-id="element-71">
            60<span className="text-lg text-primary-200" data-id="element-72">%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-1.5 mt-3" data-id="element-73">
            <div className="bg-emerald-400 h-1.5 rounded-full" style={{
            width: '60%'
          }} data-id="element-74" />
          </div>
        </div>
      </div>

      {/* Path Selector */}
      <div data-id="element-75">
        <h2 className="text-lg font-semibold text-surface-900 mb-4" data-id="element-76">
          Explore Career Paths
        </h2>
        <div className="flex overflow-x-auto pb-4 hide-scrollbar gap-4" data-id="element-77">
          {careerPaths.map(path => <button key={path.id} onClick={() => setSelectedPathId(path.id)} className={`flex-shrink-0 w-64 p-4 rounded-xl border text-left transition-all ${selectedPathId === path.id ? 'bg-primary-50 border-primary-500 shadow-md ring-1 ring-primary-500' : 'bg-white border-surface-200 hover:border-primary-300 hover:bg-surface-50'}`} data-id="element-78">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${selectedPathId === path.id ? 'bg-primary-600 text-white' : 'bg-surface-100 text-surface-600'}`} data-id="element-79">
                <path.icon className="w-5 h-5" data-id="element-80" />
              </div>
              <h3 className={`font-semibold text-sm mb-1 ${selectedPathId === path.id ? 'text-primary-900' : 'text-surface-900'}`} data-id="element-81">
                {path.title}
              </h3>
              <p className="text-xs text-surface-500 font-medium" data-id="element-82">
                End Goal:{' '}
                <span className={selectedPathId === path.id ? 'text-primary-700' : 'text-emerald-600'} data-id="element-83">
                  {path.endSalary}
                </span>
              </p>
            </button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-84">
        {/* 10-Year Roadmap */}
        <div className="lg:col-span-2" data-id="element-85">
          <h2 className="text-lg font-semibold text-surface-900 mb-6" data-id="element-86">
            10-Year Visual Roadmap
          </h2>

          <div className="bg-white p-6 sm:p-8 rounded-2xl border border-surface-200 shadow-sm" data-id="element-87">
            <div className="relative" data-id="element-88">
              {/* Vertical Line */}
              <div className="absolute left-[19px] top-4 bottom-4 w-1 bg-primary-100 rounded-full" data-id="element-89" />

              <div className="space-y-8" data-id="element-90">
                <AnimatePresence mode="wait" data-id="element-91">
                  {selectedPath.roadmap.map((phase, index) => <motion.div key={`${selectedPath.id}-${index}`} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  delay: index * 0.1,
                  duration: 0.3
                }} className="relative flex gap-6" data-id="element-92">
                      {/* Node */}
                      <div className="relative z-10 flex-shrink-0 bg-white pt-1" data-id="element-93">
                        <div className={`w-10 h-10 rounded-full border-4 flex items-center justify-center font-bold text-sm ${index === 0 ? 'border-primary-200 bg-primary-600 text-white' : 'border-primary-100 bg-white text-primary-600'}`} data-id="element-94">
                          {index + 1}
                        </div>
                      </div>

                      {/* Content Card */}
                      <div className="flex-1 bg-surface-50 rounded-xl p-5 border border-surface-200 hover:border-primary-300 hover:shadow-md transition-all" data-id="element-95">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2" data-id="element-96">
                          <div data-id="element-97">
                            <span className="text-xs font-bold text-primary-600 uppercase tracking-wider" data-id="element-98">
                              {phase.phase}
                            </span>
                            <h3 className="text-lg font-bold text-surface-900 mt-1" data-id="element-99">
                              {phase.role}
                            </h3>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 text-sm font-bold border border-emerald-100 whitespace-nowrap" data-id="element-100">
                            {phase.salary}
                          </span>
                        </div>

                        <p className="text-sm text-surface-600 mb-4" data-id="element-101">
                          {phase.desc}
                        </p>

                        <div className="space-y-3" data-id="element-102">
                          <div data-id="element-103">
                            <span className="text-xs font-semibold text-surface-500 block mb-1.5" data-id="element-104">
                              Key Skills to Develop:
                            </span>
                            <div className="flex flex-wrap gap-1.5" data-id="element-105">
                              {phase.skills.map((skill, i) => <span key={i} className="px-2 py-1 bg-white border border-surface-200 rounded text-xs font-medium text-surface-700" data-id="element-106">
                                  {skill}
                                </span>)}
                            </div>
                          </div>

                          <div data-id="element-107">
                            <span className="text-xs font-semibold text-surface-500 block mb-1.5" data-id="element-108">
                              Credentials Needed:
                            </span>
                            <div className="flex flex-wrap gap-2" data-id="element-109">
                              {phase.certs.map((cert, i) => <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs font-semibold border border-primary-100" data-id="element-110">
                                  <AwardIcon className="w-3 h-3" data-id="element-111" />
                                  {cert}
                                </span>)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6" data-id="element-112">
          <div className="bg-white p-6 rounded-2xl border border-surface-200 shadow-sm sticky top-24" data-id="element-113">
            <h2 className="text-lg font-semibold text-surface-900 mb-4" data-id="element-114">
              Career Tools
            </h2>
            <div className="space-y-3" data-id="element-115">
              <button className="w-full flex items-start gap-4 p-4 rounded-xl border border-surface-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left group" data-id="element-116">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors" data-id="element-117">
                  <FileTextIcon className="w-5 h-5 text-primary-600" data-id="element-118" />
                </div>
                <div data-id="element-119">
                  <h3 className="text-sm font-semibold text-surface-900" data-id="element-120">
                    Resume Builder
                  </h3>
                  <p className="text-xs text-surface-500 mt-0.5" data-id="element-121">
                    Auto-generate from your profile
                  </p>
                </div>
              </button>

              <button className="w-full flex items-start gap-4 p-4 rounded-xl border border-surface-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left group" data-id="element-122">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors" data-id="element-123">
                  <MessageSquareIcon className="w-5 h-5 text-primary-600" data-id="element-124" />
                </div>
                <div data-id="element-125">
                  <h3 className="text-sm font-semibold text-surface-900" data-id="element-126">
                    AI Interview Prep
                  </h3>
                  <p className="text-xs text-surface-500 mt-0.5" data-id="element-127">
                    Practice with simulated scenarios
                  </p>
                </div>
              </button>

              <button className="w-full flex items-start gap-4 p-4 rounded-xl border border-surface-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left group opacity-50 cursor-not-allowed" data-id="element-128">
                <div className="w-10 h-10 rounded-lg bg-surface-100 flex items-center justify-center flex-shrink-0" data-id="element-129">
                  <TargetIcon className="w-5 h-5 text-surface-500" data-id="element-130" />
                </div>
                <div data-id="element-131">
                  <h3 className="text-sm font-semibold text-surface-900" data-id="element-132">
                    Portfolio Generator
                  </h3>
                  <p className="text-xs text-surface-500 mt-0.5" data-id="element-133">
                    Unlocks at Phase 4
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}